import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchAllQuizes, fetchUserQuizProgress } from '@/services/api/quiz';
import { fetchCourse, exportCourseGrades, exportCourse } from '@/services/api/course';
import { Quiz } from '@/interfaces/Quiz';
import { Course as CourseData } from '@/interfaces/Course';
import { useAuth } from '@/hooks/useAuth';
import { fetchUserRoleInCourse } from '@/services/api/user';
import { Post } from '@/interfaces/Post';
import { fetchPostsByCourse } from '@/services/api/post';
import Footer from '@/components/footer/Footer';
import Header from '@/components/header/Header';
import ProgressXpBar from '@/components/utility/ProgressXpBar';
import Button from '@/components/utility/Button';
import ExercisesGroup from '@/components/course/ExercisesGroup';
import PostThreadCourse from '@/components/course/PostThreadCourse';
import NewPostForm from '@/components/course/NewPostForm';
import ReplyForm from '@/components/course/ReplyForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';

export default function Course() {
  const [Quizes, setQuizes] = useState<Quiz[]>([]);
  const [Course, setCourse] = useState<CourseData | null>(null);
  const [userCourseRole, setUserCourseRole] = useState<number | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(true);
  const [isNewPostModalOpen, setIsNewPostModalOpen] = useState(false);
  const [replyingToPostId, setReplyingToPostId] = useState<string | null>(null);
  const [isCourseLoading, setIsCourseLoading] = useState(true);
  const { courseId } = useParams();
  const { user, isLoading: isAuthLoading } = useAuth();
  const [isExportingGrades, setIsExportingGrades] = useState(false);
  const [isExportingCourse, setIsExportingCourse] = useState(false);

  const navigate = useNavigate();

  const topLevelPosts = posts.filter(p => p.id_parent === null);
  const replies = posts.filter(p => p.id_parent !== null);
  const getRepliesForPost = (postId: string) => {
    return replies.filter(reply => reply.id_parent === postId);
  };

  const [quizProgress, setQuizProgress] = useState({
    userScore: 0,
    maxScore: 0,
    percentage: 0,
  });

  useEffect(() => {
    async function fetchData() {
      if (courseId && user?.id) {
        try {
          const [quizesResponse, courseInfoResponse, userCourseData] = await Promise.all([
            fetchAllQuizes(courseId),
            fetchCourse(courseId),
            fetchUserRoleInCourse(user.id.toString(), courseId)
          ]);

          setQuizes(quizesResponse);
          setCourse(courseInfoResponse.data);
          setUserCourseRole(userCourseData.role);

          if (quizesResponse.length > 0) {
            const progressPromises = quizesResponse.map((quiz: { id: string; }) =>
              fetchUserQuizProgress(quiz.id, user.id.toString())
            );
            const progresses = await Promise.all(progressPromises);

            let totalUserScore = 0;
            let totalQuizMaxScore = 0;

            progresses.forEach(p => {
              totalUserScore += p.user_max_score || 0;
              totalQuizMaxScore += p.quiz_max_score || 0;
            });

            const percentage = totalQuizMaxScore > 0
              ? (totalUserScore / totalQuizMaxScore) * 100
              : 0;

            setQuizProgress({
              userScore: totalUserScore,
              maxScore: totalQuizMaxScore,
              percentage: percentage,
            });
          }

          const fetchedPosts = await fetchPostsByCourse(courseId);
          setPosts(fetchedPosts);
        } catch (error) {
          console.error("Failed to fetch course data:", error);
          setUserCourseRole(null);
        } finally {
          setIsCourseLoading(false);
          setIsLoadingPosts(false);
        }
      } else if (!isAuthLoading) {
        setIsCourseLoading(false);
        setIsLoadingPosts(false);
      }
    }

    fetchData();
  }, [courseId, user, isAuthLoading]);

  const handleOpenNewPostModal = () => {
      setIsNewPostModalOpen(true);
    };

    const handleCloseNewPostModal = () => {
      setIsNewPostModalOpen(false);
    };

    const handleOpenReplyModal = (postId: string) => {
      setReplyingToPostId(postId);
    };

    const handleCloseReplyModal = () => {
      setReplyingToPostId(null);
    };

    const handlePostCreated = (newPost: Post) => {
      // Adiciona o novo post no topo da lista para feedback imediato
      setPosts(prevPosts => [newPost, ...prevPosts]);
      // A lógica de agrupamento será executada automaticamente na próxima renderização
    };

  const isUserTheCourseTeacher = userCourseRole === 2;

  if (isCourseLoading || isAuthLoading) {
    // Show a loading spinner or placeholder while fetching
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading...</p>
      </div>
    );
  }

  const handleImportSuccess = (res: any) => {
    alert('Usuários importados com sucesso!');
    console.log(res.data);
  };

  const handleImportError = (err: any) => {
    alert('Falha ao importar.');
    console.error(err);
  };

  const handleExportGrades = async () => {
    if (!courseId || isExportingGrades) return;

    setIsExportingGrades(true);

    try {
      const response = await exportCourseGrades(courseId);
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : 'notas_do_curso.csv';

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);

      link.click();

      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      window.URL.revokeObjectURL(url);

    } catch (error) {
      alert('Falha ao exportar as notas. Verifique se você tem permissão e tente novamente.');
    } finally {
      setIsExportingGrades(false);
    }
  };

  const handleExportCourse = async () => {
    if (!courseId || isExportingCourse) return;

    setIsExportingCourse(true);

    try {
      const response = await exportCourse(courseId);

      const contentDisposition = response.headers['content-disposition'];

      let filename = Course?.name
        ? `${Course.name}.json`
        : 'curso.json';

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match?.[1]) {
          filename = match[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }

      window.URL.revokeObjectURL(url);

    } catch (error) {
      alert('Falha ao exportar o curso. Verifique sua permissão e tente novamente.');
      console.error(error);
    } finally {
      setIsExportingCourse(false);
    }
  };

  return (
    <div className="w-full overflow-x-hidden gap-6 bg-grid-pattern">
      <Header/>

      {isNewPostModalOpen && (
        <NewPostForm
          courseId={courseId!}
          onClose={handleCloseNewPostModal}
          onPostCreated={handlePostCreated}
        />
      )}

      {replyingToPostId && (
        <ReplyForm
          courseId={courseId!}
          parentId={replyingToPostId}
          onClose={handleCloseReplyModal}
          onReplyCreated={handlePostCreated}
        />
      )}

      <div className="flex flex-col mt-8 md:mt-12 justify-between items-center w-full">
        {Course && (
          <div className="w-full max-w-[1200px] flex flex-col justify-center px-8">
            <h2 className="w-full my-8 text-5xl text-center font-bold text-cinza-900">
              {Course.name}
            </h2>

            <ProgressXpBar
              text="Seu progresso"
              progress={quizProgress.percentage}
              value={quizProgress.userScore}
              maxValue={quizProgress.maxScore}
            />
          </div>
        )}

        <div className="w-full my-8 flex justify-end px-8 md:px-20">

        </div>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-12 px-8 md:px-20 mb-20">
        <div className="flex flex-col flex-1 min-w-[60%]">
          {/* Seção de Questionários */}
          <div className="flex flex-col w-full mb-8">
            <div className="w-full flex flex-row justify-between mb-8">
              <div className="flex flex-row flex-wrap gap-5 sm:gap-8">
                <div className="flex w-full sm:w-fit py-4 px-8 items-center justify-center bg-roxo-300 shadow-default-roxo-500">
                  <p className="text-lg sm:text-xl font-bold text-[#bab1fc]">
                    Questionários
                  </p>
                </div>

                {user?.role === 2 &&
                  <button onClick={() => {navigate(`/create/quiz/${courseId}`)}} className="flex w-full sm:w-fit min-w-[200px] py-4 px-6 items-center justify-center bg-verde-300 shadow-default-verde-900 gap-4 transition-all duration-300 hover:scale-[1.03]">
                    <FontAwesomeIcon icon={faPencil} className="text-[#2f6e4e] text-2xl" />
                    <h2 className="text-lg sm:text-xl font-bold text-[#2f6e4e]">Novo questionário</h2>
                  </button>
                }
              </div>

              <div className="flex flex-row flex-wrap gap-5 sm:gap-8">
                {isUserTheCourseTeacher && (
                  <>
                    <Button
                      upload
                      uploadUrl="/user"
                      fieldName="file"
                      courseId={courseId!}
                      onUploadSuccess={handleImportSuccess}
                      onUploadError={handleImportError}
                      variant='default'
                      className="text-lg"
                      text="Importar alunos"
                      size="small"
                    ></Button>

                    <Button
                      onClick={handleExportGrades}
                      disabled={isExportingGrades}
                      courseId={courseId!}
                      variant='default'
                      className="text-lg"
                      text={isExportingGrades ? "Exportando..." : "Exportar notas"}
                      size="small"
                    ></Button>

                    <Button
                      onClick={handleExportCourse}
                      disabled={isExportingCourse}
                      courseId={courseId!}
                      variant='default'
                      className="text-lg"
                      text={isExportingCourse ? "Exportando..." : "Exportar curso"}
                      size="small"
                    ></Button>
                  </>
                )}
              </div>
            </div>

            <ExercisesGroup itens={Quizes} />
          </div>

          {/* Seção do Fórum/Posts */}
          <div className="flex flex-col w-full">
            <div className="flex justify-between items-center w-full p-6 bg-laranja shadow-default-laranja">
              <h2 className="text-2xl font-bold text-[#97581F]">Fórum de Discussão</h2>
              <Button variant='tertiary' text="Novo Post" size="medium" onClick={handleOpenNewPostModal} />
            </div>

            <div className="p-4 bg-gray-100 shadow-default-cinza-300">
              {isLoadingPosts ? (
                <p className="text-center text-cinza-900 py-4">Carregando posts...</p>
              ) : topLevelPosts.length > 0 ? (
                topLevelPosts.map(post => (
                  <PostThreadCourse
                    key={post.id}
                    post={post}
                    replies={getRepliesForPost(post.id)}
                    onReply={handleOpenReplyModal}
                  />
                ))
              ) : (
                <p className="text-center text-cinza-900 py-4">Ainda não há nenhuma discussão no fórum.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
